import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

const LanguageContainer = styled.div`
  position: relative;
  cursor: pointer;
  margin: ${(props) => (props.$flush ? '0' : '0 1rem')};
  z-index: 2000;
  ${(props) =>
    props.$flush &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    line-height: 0;
  `}
`;

const FlagImage = styled.img`
  display: block;
  width: ${(props) => props.$size ?? 24}px;
  height: ${(props) => props.$size ?? 24}px;
  border-radius: 50%;
  transition: transform 0.3s ease;
  object-fit: cover;
  flex-shrink: 0;
`;

const PopupContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 0.5rem;
  margin-top: 0.5rem;
  display: ${props => props.show ? 'block' : 'none'};
  z-index: 2000;
  min-width: 120px;
  
  @media (max-width: 768px) {
    left: auto;
    right: 0;
    transform: none;
    min-width: 100px;
    z-index: 2000;
  }
`;

const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  color: #333;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
    border-radius: 4px;
  }
  
  &.selected {
    background-color: #e8f5e8;
    color: #14464F;
    font-weight: 600;
  }
  
  img {
    margin-right: 8px;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 16px;
    min-height: 44px;
    
    img {
      pointer-events: none;
    }
  }
`;

const LanguageSelector = ({
  onPopupToggle,
  isOpen,
  isMobile = false,
  /** Sharper flags on retina (e.g. welcome header) */
  highResFlags = false,
  /** No side margin (e.g. when parent is transparent pill) */
  flushChrome = false,
}) => {
  const { language, setLanguage } = useLanguage();
  const containerRef = useRef(null);
  const flagSrcWidth = highResFlags ? 160 : 40;
  const mainFlagPx = highResFlags ? 36 : 24;
  const optionFlagPx = highResFlags ? 28 : 20;

  const handleFlagClick = (event) => {
    event.stopPropagation();
    onPopupToggle();
  };

  const handleLanguageChange = (newLang) => {
    console.log('Language change triggered:', newLang);
    setLanguage(newLang);
    localStorage.setItem('preferredLanguage', newLang);
    
    if (isMobile) {
      // For mobile, show a brief success state before closing
      setTimeout(() => {
        onPopupToggle();
      }, 200);
      return;
    }
    
    // For desktop, close immediately
    onPopupToggle();
  };

  const handleLanguageOptionClick = (newLang) => {
    return (event) => {
      console.log('Language option clicked:', newLang, event.type);
      event.preventDefault();
      event.stopPropagation();
      handleLanguageChange(newLang);
    };
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Only close if we're not clicking on a language option
        if (!event.target.closest('.language-option')) {
          onPopupToggle();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onPopupToggle]);

  return (
    <LanguageContainer ref={containerRef} $flush={flushChrome}>
      <FlagImage
        $size={mainFlagPx}
        src={`https://flagcdn.com/w${flagSrcWidth}/${language === 'en' ? 'us' : 'do'}.png`}
        alt={language === 'en' ? 'English' : 'Español'}
        onClick={handleFlagClick}
      />
      <PopupContainer show={isOpen}>
        <LanguageOption 
          onClick={handleLanguageOptionClick('en')}
          onMouseDown={handleLanguageOptionClick('en')}
          className={`language-option ${language === 'en' ? 'selected' : ''}`}
        >
          <FlagImage
            $size={optionFlagPx}
            src={`https://flagcdn.com/w${flagSrcWidth}/us.png`}
            alt="English"
          />
          English
        </LanguageOption>
        <LanguageOption 
          onClick={handleLanguageOptionClick('es')}
          onMouseDown={handleLanguageOptionClick('es')}
          className={`language-option ${language === 'es' ? 'selected' : ''}`}
        >
          <FlagImage
            $size={optionFlagPx}
            src={`https://flagcdn.com/w${flagSrcWidth}/do.png`}
            alt="Español"
          />
          Español
        </LanguageOption>
      </PopupContainer>
    </LanguageContainer>
  );
};

export default LanguageSelector; 